import { useCallback, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { Namespaces } from 'types/locales';
import PaginationNav from 'components/control/pagination-nav';
import { CollectionsTabs, collectionsPanelId, collectionsTabId } from './collections-tabs';
import { CollectionsListingGrid } from './collections-listing-grid';
import { CollectionsSavedSearches } from './collections-saved-searches';
import { useCollectionsFavourites } from './use-collections-favourites';
import { useCollectionsHistory } from './use-collections-history';
import { useSavedSearchesCount } from './use-saved-searches-count';
import type { CollectionsTabId } from './collections-tabs';
import type { Filter } from 'contexts/preferences/listing-params/types';
import styles from './collections-pane.module.scss';

export type CollectionsPaneProps = {
  /**
   * Applies a saved search to the page. Page-specific, because where a search
   * applies depends on how it was saved, so each page passes its own handler.
   */
  onApplySavedSearch: (filter: Filter) => void;
  /**
   * Which tab to show first. Defaults to Favourites. Both pages unmount this
   * component when the user leaves Collections, so this applies every time
   * Collections is opened, not only on the first open.
   */
  initialTab?: CollectionsTabId;
  /** Called when the user picks a different tab. */
  onTabChange?: (tab: CollectionsTabId) => void;
};

/**
 * Collections destination of the area page: the signed-in user's saved things,
 * split across Favourites, Saved Searches, and Recently Viewed. One section is
 * visible at a time, chosen by a segmented control. Each section's list loads
 * only while its tab is shown, and pages through its own numbered nav.
 */
export function CollectionsPane({ onApplySavedSearch, initialTab = 'favourites', onTabChange }: CollectionsPaneProps) {
  const { t } = useTranslation(Namespaces.COMMON);
  const [activeTab, setActiveTab] = useState<CollectionsTabId>(initialTab);

  const handleTabChange = useCallback((tab: CollectionsTabId) => {
    setActiveTab(tab);
    onTabChange?.(tab);
  }, [onTabChange]);

  const favourites = useCollectionsFavourites(activeTab === 'favourites');
  const history = useCollectionsHistory(activeTab === 'history');
  const savedSearchesCount = useSavedSearchesCount();

  // Each count is shown as soon as it is known. They used to be held back until
  // both had loaded, so they appeared together. That stopped working when the
  // pane gained `initialTab`: the favourites count only loads while the
  // Favourites tab is active, so opening on Saved Searches meant neither count
  // ever appeared.
  const tabs = [
    // The favourites count comes from the saved-listings endpoint's `meta`. The
    // saved searches count comes from its own list, fetched straight away.
    { id: 'favourites' as const, label: t('area_page_v2.collections.tabs.favourites', { defaultValue: 'Favourites' }), count: favourites.count },
    { id: 'saved-searches' as const, label: t('area_page_v2.collections.tabs.saved_searches', { defaultValue: 'Saved Searches' }), count: savedSearchesCount },
    { id: 'history' as const, label: t('area_page_v2.collections.tabs.history', { defaultValue: 'Recently Viewed' }) },
  ];

  return (
    <section
      className={styles.root}
      data-testid="AreaPageV2-CollectionsPane"
      aria-label={t('area_page_v2.collections.title', { defaultValue: 'Collections' })}
    >
      <div className={styles.header}>
        <h1 className={styles.title}>
          {t('area_page_v2.collections.title', { defaultValue: 'Collections' })}
        </h1>
      </div>

      <div className={styles.tabsBar}>
        <CollectionsTabs
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          label={t('area_page_v2.collections.tabs_label', { defaultValue: 'Collections sections' })}
        />
      </div>

      <div className={styles.content}>
        {activeTab === 'favourites' && (
          <div
            id={collectionsPanelId('favourites')}
            role="tabpanel"
            aria-labelledby={collectionsTabId('favourites')}
          >
            <CollectionsListingGrid
              listings={favourites.listings}
              isLoading={favourites.isLoading}
              emptyMessage={t('area_page_v2.collections.favourites_empty', { defaultValue: 'No favourite listings yet.' })}
              hasError={favourites.hasError}
              errorMessage={t('area_page_v2.collections.listings_error', { defaultValue: 'Failed to fetch listings.' })}
              testId="AreaPageV2-Collections-FavouritesGrid"
            />
            {favourites.totalPages > 1 && (
              <div className={styles.pagination}>
                <PaginationNav
                  pageNumber={favourites.pageNumber}
                  totalPages={favourites.totalPages}
                  setPageNumber={favourites.setPageNumber}
                />
              </div>
            )}
          </div>
        )}

        {activeTab === 'saved-searches' && (
          <div
            id={collectionsPanelId('saved-searches')}
            role="tabpanel"
            aria-labelledby={collectionsTabId('saved-searches')}
          >
            <CollectionsSavedSearches isActive onApplySavedSearch={onApplySavedSearch} />
          </div>
        )}

        {activeTab === 'history' && (
          <div
            id={collectionsPanelId('history')}
            role="tabpanel"
            aria-labelledby={collectionsTabId('history')}
          >
            <CollectionsListingGrid
              listings={history.listings}
              isLoading={history.isLoading}
              emptyMessage={t('area_page_v2.collections.history_empty', { defaultValue: 'No browsing history yet.' })}
              hasError={history.hasError}
              errorMessage={t('area_page_v2.collections.listings_error', { defaultValue: 'Failed to fetch listings.' })}
              testId="AreaPageV2-Collections-HistoryGrid"
            />
          </div>
        )}
      </div>
    </section>
  );
}
